import { useState, useEffect, useCallback, useMemo } from 'react';

export type ThemePreference = 'light' | 'dark' | 'auto';

const THEME_KEY = 'theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function loadTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'auto') {
      return raw;
    }
  } catch (e) {
    console.warn('Failed to read theme from localStorage', e);
  }
  return 'auto';
}

function applyTheme(resolved: 'light' | 'dark'): void {
  document.documentElement.setAttribute('data-theme', resolved);
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemePreference>(loadTheme);
  const [systemIsDark, setSystemIsDark] = useState(() => window.matchMedia(MEDIA_QUERY).matches);

  const resolvedTheme = useMemo<'light' | 'dark'>(() => {
    if (theme === 'light' || theme === 'dark') return theme;
    return systemIsDark ? 'dark' : 'light';
  }, [theme, systemIsDark]);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (theme !== 'auto') {
      return;
    }

    const mql = window.matchMedia(MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setSystemIsDark(e.matches);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch (e) {
      console.warn('Failed to save theme to localStorage', e);
    }
  }, []);

  return { theme, resolvedTheme, setTheme } as const;
}
